import React, { useEffect, useState } from 'react';
import { Position, NodeProps } from '@xyflow/react';
import { TextCustom } from '../../../ui/TextCustom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { rootState } from '../../../../redux/store';
import { CustomNodeDesign } from './CustomNodeDesign';
import { CustomHandle } from './CustomHandle';
import { ICommit, IDepartments, IStatus, IWebSocketData } from '../../../../interface/interfaces';
import { CustomNodeItem } from './CustomNodeItem';
import { ToolTipWrapper } from '../../../layout/ToolTipWrapper/ToolTipWrapper';
import { ExecButton } from '../../../ui/ExecButton/ExecButton';
import { fetchCommitHistoryPending } from '../CommitHistory/commitHistory.slice';
import { addSourceTargetDepartment } from '../ViewportDepartmentNetwork/sourceTargetDepartment.slice';
import { departmentInitialState } from '../../../../initials/initials';
import { openModal } from '../../Modal/modal.slice';

type IPropsData = {
    bgColor: string;
    borderColor: string;
    node: IDepartments;
    rootDepartment: IDepartments;
    sendWebsocketMessage: (data: string) => void;
};

export const CustomNode = (props: NodeProps) => {
    const { bgColor, borderColor, node, rootDepartment, sendWebsocketMessage } = props.data as IPropsData;

    const { dataAllChildrensPerNode } = useAppSelector((state: rootState) => state.filteredChildrenNodesPerSelection);
    const { commitsLoading, commitsData, commitsError } = useAppSelector((state: rootState) => state.commits);
    const { statusData, statusError, statusLoading } = useAppSelector((state: rootState) => state.status);
    const { project } = useAppSelector((state: rootState) => state.project);
    const { userAndTokenData } = useAppSelector((state: rootState) => state.loginUserAndTokenData);

    const dispatch = useAppDispatch();

    const [currentCommit, setCurrentCommit] = useState<ICommit | undefined>(undefined);
    const [currentStatus, setCurrentStatus] = useState<IStatus | undefined>(undefined);

    // find commit with same department id
    useEffect(() => {
        const selectedCommit = commitsData.find((item: ICommit) => {
            return item.DepartmentId === node.DepartmentId;
        });
        setCurrentCommit(selectedCommit);
    }, [commitsData, node.DepartmentId, currentCommit]);

    // find status with same department id
    useEffect(() => {
        const selectedStatus = statusData.find((item: IStatus) => {
            return item.DepartmentId === node.DepartmentId;
        });
        setCurrentStatus(selectedStatus);
    }, [statusData, node.DepartmentId, currentStatus]);

    const onOpenCommit = () => {
        const data: IWebSocketData = {
            defaultLocation: '',
            parameter: 'Commitordner oeffnen',
            data: [],
            department: '',
            projectID: project?._id || '',
            token: userAndTokenData?.token || '',
            filePath: currentCommit?.FilePath || '',
        };

        sendWebsocketMessage(JSON.stringify(data));
    };

    const onCommitHistory = (nodeType: string | undefined) => {
        let tempSourceDepartment: IDepartments = departmentInitialState;
        let tempTargetDepartment = departmentInitialState;

        if (nodeType) {
            if (nodeType === 'In') {
                tempSourceDepartment = node;
                tempTargetDepartment = rootDepartment;
            } else if (nodeType === 'Out') {
                tempSourceDepartment = rootDepartment;
                tempTargetDepartment = node;
            }
        }

        // console.log('SOURCE: ', tempSourceDepartment, 'TARGET: ', tempTargetDepartment)
        if (userAndTokenData) {
            dispatch(
                fetchCommitHistoryPending({
                    url: `project/${project?._id}/DepartmentCommitsHistory`,
                    token: userAndTokenData?.token,
                    body: {
                        sourceDepartmentId: tempSourceDepartment.DepartmentId,
                        targetDepartmentId: tempTargetDepartment.DepartmentId,
                    },
                }),
            );
        }

        dispatch(addSourceTargetDepartment({ source: tempSourceDepartment, target: tempTargetDepartment }));
        dispatch(openModal("commitHistoryModal"));
    };

    return (
        <>
            {(node.Type === 'default' || node.Type === 'Out') && <CustomHandle type={'target'} position={Position.Left} bgColor={bgColor as string} borderColor={borderColor} />}
            {(node.Type === 'default' || node.Type === 'In') && <CustomHandle type={'source'} position={Position.Right} bgColor={bgColor as string} borderColor={borderColor} />}

            <CustomNodeDesign bgColor={bgColor} borderColor={borderColor}>
                <TextCustom weight={700} color="dark">
                    {node.Label}
                </TextCustom>
                <div style={{ width: '100%', borderBottom: '1px solid rgba(0,0,0,.25)' }}></div>
                <ToolTipWrapper
                    stretch={true}
                    toolTipContent={{
                        title: 'Status',
                        toolTip: [
                            { label: 'State', value: `${currentStatus?.Status || 'MISSING_DATA'}` },
                            {
                                label: 'Descrition',
                                value: `${'UNKNOWN: no status yet, NEW_DATA: new data, NOT_OK: data problem, OK: verything is fine, MISSING_DATA: there is some missing data'}`,
                            },
                        ],
                        color: 'dark',
                        iconName: `${currentStatus?.Status || 'MISSING_DATA'}`,
                        iconSize: 2,
                        fixedSize: true,
                    }}
                >
                    {/* <IconComponent size={1.6} iconName={node.Status}/> */}
                    <CustomNodeItem iconName={'Active'} label={'Status'} loading={statusLoading} hasEndIcon endIconName={currentStatus?.Status || 'MISSING_DATA'} />
                </ToolTipWrapper>

                {node.Type !== 'default' && (
                    <>
                        <CustomNodeItem iconName={'User'} label={'User'} loading={commitsLoading} text={currentCommit?.User || 'No Versions'} />
                        <CustomNodeItem iconName={'Text'} label={'Text'} loading={commitsLoading} text={currentCommit?.CommitText || 'No Versions'} />
                        <ToolTipWrapper
                            stretch={true}
                            toolTipContent={{
                                title: 'Commits content',
                                toolTip: [
                                    {
                                        label: 'Content',
                                        value: currentCommit?.Content
                                            ? currentCommit?.Content.map((content: string, idx: number) => {
                                                  return <div key={idx}>{content}</div>;
                                              })
                                            : 'NO CONTENT',
                                    },
                                ],
                                color: 'dark',
                                iconName: `Commit_green_arrow`,
                                iconSize: 2,
                                fixedSize: false,
                            }}
                        >
                            <CustomNodeItem iconName={'Commit'} label={'Version'} loading={commitsLoading} text={currentCommit?.CommitId || 'No Versions'} />
                        </ToolTipWrapper>
                        <div style={{ width: '100%', borderBottom: '1px solid rgba(0,0,0,.25)' }}></div>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'end', gap: '.5rem' }}>
                            {/* <ExecButton  size='s' label={'TEST'} iconName={'Send'} func={()=>console.log('NODE: ', node, rootDepartment)}/> */}
                            <ExecButton size="s" label={'Open version'} iconName={'OpenCommit'} func={() => onOpenCommit()} />
                            <ExecButton size="s" label={'Version history'} iconName={'CommitHistory'} func={() => onCommitHistory(node.Type)} />
                        </div>
                    </>
                )}
            </CustomNodeDesign>
        </>
    );
};
